import { InstanceDeserializer } from '@gametailors/lp-studio-connector';
import { generateUid, sampleRandom } from '@gametailors/web-core/misc';
import {
  LpFinishMissionActivity,
  LpRidAlgoritmeActivity,
  LpRidDicteeActivity,
  LpRidLeeslijstActivity,
  LpRIDLettermixActivity,
  LpRIDPasDeRegelToeActivity,
  LpRidWisselklankenActivity,
  LpRIDWoord,
  LpRidWoordpadActivity,
  LpRidZinlezenActivity,
  LpRidZinlezenModeFocuslezen,
  LpRidZinlezenModeLichtkrant,
  LpRidZinlezenModeMistlezen,
  LpRidZinlezenModeNormaal,
  LpSchema,
  LpSimpleActivityChange,
} from '../../../typings/schema';
import { STUDIO_API_DATA_PACKAGE_PROVIDER } from '../../leerplus/rid-game.provider';

function makeFinishMissionActivity(): LpFinishMissionActivity {
  return {
    uid: generateUid(),
    dataType: 'FinishMissionActivity',
    name: 'Finish',
    onTrigger: [],
  };
}

function makeActivityChange(): LpSimpleActivityChange {
  return {
    uid: generateUid(),
    dataType: 'SimpleActivityChange',
    nextActivity: makeFinishMissionActivity(),
  };
}

async function getWordList(): Promise<LpRIDWoord[]> {
  return (await getInstances()).filter((i) => i.dataType === 'RIDWoord');
}

let cache: LpSchema['all'][] | undefined = undefined;
async function getInstances(): Promise<LpSchema['all'][]> {
  if (cache) return cache;

  const casApi = STUDIO_API_DATA_PACKAGE_PROVIDER;

  const [dp, schema, files] = await Promise.all([casApi.getDataPackage(), casApi.getSchema(), casApi.getFileMetas()]);
  const deserializer = new InstanceDeserializer<LpSchema['all']>(schema, files);
  cache = deserializer.deserializeMany(dp.instances);
  return cache;
}

export class MockGenerators {
  public static async generateLeeslijstActivity(
    mode: { variant: 'NO_TIME' } | { variant: 'STOPWATCH' } | { variant: 'TIMER'; duration: number } | { variant: 'CATEGORISE' },
  ): Promise<LpRidLeeslijstActivity> {
    const words = sampleRandom(await getWordList(), 18);

    function generateMode(
      mode: { variant: 'NO_TIME' } | { variant: 'STOPWATCH' } | { variant: 'TIMER'; duration: number } | { variant: 'CATEGORISE' },
    ): LpRidLeeslijstActivity['mode'] {
      switch (mode.variant) {
        case 'CATEGORISE':
          return { uid: generateUid(), dataType: 'RidLeeslijstModeCategorise', category: 'fruit', correctWords: sampleRandom(words, 3) };
        case 'NO_TIME':
          return { uid: generateUid(), dataType: 'RidLeeslijstModeNoTime' };
        case 'STOPWATCH':
          return { uid: generateUid(), dataType: 'RidLeeslijstModeStopwatch' };
        case 'TIMER':
          return { uid: generateUid(), dataType: 'RidLeeslijstModeTimer', duration: mode.duration };
      }
    }

    return {
      uid: generateUid(),
      dataType: 'RidLeeslijstActivity',
      name: `Leeslijst (${mode.variant})`,
      mode: generateMode(mode),
      words,
      activityChange: makeActivityChange(),
    };
  }

  public static async generateLettermixActivity(): Promise<LpRIDLettermixActivity> {
    const lettermixes = (await getInstances()).filter((i) => i.dataType === 'RIDLettermix');
    return {
      uid: generateUid(),
      dataType: 'RIDLettermixActivity',
      name: 'Lettermix',
      matrixes: sampleRandom(lettermixes, 3),
      activityChange: makeActivityChange(),
    };
  }

  public static async generateZinlezenActivity(
    mode: 'NORMAAL' | 'LICHTKRANT' | 'FOCUSLEZEN' | 'MISTLEZEN',
  ): Promise<LpRidZinlezenActivity> {
    const sentences = (await getInstances()).filter((i) => i.dataType === 'RIDZin');
    return {
      uid: generateUid(),
      dataType: 'RidZinlezenActivity',
      name: 'Zinlezen',
      mode: {
        NORMAAL: {
          uid: generateUid(),
          dataType: 'RidZinlezenModeNormaal',
        } satisfies LpRidZinlezenModeNormaal,
        LICHTKRANT: {
          uid: generateUid(),
          dataType: 'RidZinlezenModeLichtkrant',
        } satisfies LpRidZinlezenModeLichtkrant,
        FOCUSLEZEN: {
          uid: generateUid(),
          dataType: 'RidZinlezenModeFocuslezen',
        } satisfies LpRidZinlezenModeFocuslezen,
        MISTLEZEN: {
          uid: generateUid(),
          dataType: 'RidZinlezenModeMistlezen',
        } satisfies LpRidZinlezenModeMistlezen,
      }[mode],
      sentences: [{ dataType: 'RidSentenceList', uid: generateUid(), sentences: sampleRandom(sentences, 2) }],
      activityChange: makeActivityChange(),
    };
  }

  public static async generateWoordpadActivity(): Promise<LpRidWoordpadActivity> {
    const words = await getWordList();
    return {
      uid: generateUid(),
      dataType: 'RidWoordpadActivity',
      name: 'Woordpad',
      startWord: words.find((w) => w.woord === 'leeuw')!,
      path: [
        words.find((w) => w.woord === 'meeuw')!,
        words.find((w) => w.woord === 'mij')!,
        words.find((w) => w.woord === 'moe')!,
        words.find((w) => w.woord === 'boe')!,
        words.find((w) => w.woord === 'boem')!,
        words.find((w) => w.woord === 'bloem')!,
      ],
      activityChange: makeActivityChange(),
    };
  }

  public static async generateDicteeActivity(medium: 'Digitaal' | 'Fysiek'): Promise<LpRidDicteeActivity> {
    const words = (await getWordList()).filter((w) => w.audio && w.audio.url.length > 0);

    return {
      uid: generateUid(),
      dataType: 'RidDicteeActivity',
      name: 'Dictee',
      medium,
      wordsOrSentences: {
        uid: generateUid(),
        dataType: 'RidWords',
        words: sampleRandom(words, 5),
      },
      activityChange: makeActivityChange(),
    };
  }

  public static async generateAlgoritmeActivity(): Promise<LpRidAlgoritmeActivity> {
    return {
      uid: generateUid(),
      dataType: 'RidAlgoritmeActivity',
      name: 'Algoritme',
      activityChange: makeActivityChange(),
    };
  }

  public static async generateWisselklankenActivity(): Promise<LpRidWisselklankenActivity> {
    const words = await getWordList();
    const wordsWithImageAndAudio = sampleRandom(
      words.filter((w) => w.audio && w.audio.url.length > 0 && w.image && w.image.url.length > 0),
      3,
    );

    return {
      uid: generateUid(),
      dataType: 'RidWisselklankenActivity',
      name: 'Wisselklanken',
      prompts: wordsWithImageAndAudio,
      activityChange: makeActivityChange(),
    };
  }

  public static async generatePasDeRegelToeActivity(): Promise<LpRIDPasDeRegelToeActivity> {
    const allWords = await getWordList();
    const filteredWords = allWords.filter((w) => w.annotatie && w.audio && w.audio.url.length > 0);
    const words = sampleRandom(filteredWords, 5);
    return {
      uid: generateUid(),
      dataType: 'RIDPasDeRegelToeActivity',
      name: 'Pas de regel toe',
      words: words,
      activityChange: makeActivityChange(),
    };
  }
}
